import { useSearchPageState } from '@klareospace/pharos-lib-front-utils';
import type { Template } from '@klareospace/pharos-lib-types';

import { usePaginatedTemplates } from './use-paginated-templates';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseCatalogModalReturn {
  error: Error | null;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  onSearchChange: (value: string) => void;
  refetch: () => void;
  searchValue: string;
  templates: Template[];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Manages catalog modal state and lazily fetches templates only when the modal
 * is open. Pass `open` from the parent `CatalogModal` so the infinite query is
 * not started until the user actually opens the dialog.
 */
export function useCatalogModal(type: string, open: boolean): UseCatalogModalReturn {
  const searchState = useSearchPageState();

  const { error, hasMore, isLoading, isLoadingMore, loadMore, refetch, templates } = usePaginatedTemplates({
    enabled: open,
    search: searchState.debouncedSearch,
    type,
  });

  return {
    error,
    hasMore,
    isLoading,
    isLoadingMore,
    loadMore,
    onSearchChange: searchState.onSearchChange,
    refetch,
    searchValue: searchState.searchValue,
    templates,
  };
}
